/** Generic detail page — reused for Courses, Sadhana, Products, Services, Research, Books. */
import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ShoppingBag, BookOpen, Calendar, Download, Check, ArrowRight, Heart } from "lucide-react";
import {
  Container, Section, Eyebrow, AccentItalic, Breadcrumb, Button,
  Badge, RatingStars, LevelBadge, PriceBlock, ArtCard, LoadingState, ErrorState, Card, CardBody,
  Tabs, TabsList, TabsTrigger, TabsContent, useToast,
} from "../../components/ui";
import { api, formatApiErrorDetail } from "../../lib/api";
import { useCart } from "../cart/CartContext";
import { useAuth } from "../auth/AuthContext";

export function DetailPage({ kind, listPath, detailPath, listLabel, addLabel = "Add to cart" }) {
  const { slug } = useParams();
  const [doc, setDoc] = useState(null);
  const [err, setErr] = useState(null);
  const [saved, setSaved] = useState(false);
  const [activeImage, setActiveImage] = useState(null);
  const [lightbox, setLightbox] = useState(false);
  const { add } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    setDoc(null); setErr(null); setActiveImage(null);
    api.get(`${listPath}/${slug}`).then((r) => { setDoc(r.data); setActiveImage(r.data.hero_image_url || null); }).catch((e) => setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  }, [listPath, slug]);

  // Close lightbox on Esc
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setLightbox(false); };
    if (lightbox) { document.body.style.overflow = "hidden"; window.addEventListener("keydown", onKey); }
    return () => { document.body.style.overflow = ""; window.removeEventListener("keydown", onKey); };
  }, [lightbox]);

  // Check wishlist state for current item
  useEffect(() => {
    if (!user || !doc) { setSaved(false); return; }
    api.get("/dashboard/wishlist").then((r) => {
      setSaved((r.data.items || []).some((w) => w.kind === kind && w.item.id === doc.id));
    }).catch(() => {});
  }, [user, doc, kind]);

  const onToggleWishlist = async () => {
    if (!user) { nav("/login?next=" + encodeURIComponent(window.location.pathname)); return; }
    try {
      const { data } = await api.post("/dashboard/wishlist/toggle", { kind, item_id: doc.id });
      setSaved(!!data.in_wishlist);
      toast({ variant: "success", title: data.in_wishlist ? "Saved to wishlist" : "Removed from wishlist", description: doc.title });
    } catch (e) {
      toast({ variant: "error", title: "Couldn't update wishlist" });
    }
  };

  if (err) return <Section padded="sm"><Container><ErrorState description={err} retry={() => window.location.reload()} /></Container></Section>;
  if (!doc) return <Section padded="sm"><Container><LoadingState /></Container></Section>;

  const onAdd = () => {
    add({ kind, item_id: doc.id, title: doc.title, slug: doc.slug, price_inr: doc.price_inr || 0, image_seed: doc.title?.length || 0 });
    toast({ variant: "success", title: "Added to cart", description: doc.title });
  };

  const onEnroll = async () => {
    if (!user) { nav("/login?next=" + encodeURIComponent(window.location.pathname)); return; }
    if (doc.is_free) {
      await api.post(`/courses/${doc.id}/enroll`);
      toast({ variant: "success", title: "Enrolled!", description: doc.title });
      nav("/dashboard/courses");
    } else { onAdd(); nav("/cart"); }
  };

  const onBook = () => nav(`/services/booking/${doc.slug}`);

  return (
    <Section padded="sm" data-testid={`detail-${kind}`}>
      <Container>
        <Breadcrumb items={[{ label: "Home", to: "/" }, { label: listLabel, to: listPath.replace("/api", "").replace(/^\//, "/") }, { label: doc.title }]} />

        <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-12 mt-8">
          <div>
            <Eyebrow>{kind.charAt(0).toUpperCase() + kind.slice(1)}{doc.level ? ` · ${doc.level}` : ""}</Eyebrow>
            <h1 className="font-display text-[clamp(34px,4.4vw,56px)] tracking-[-0.01em] leading-[1.06] mt-4">
              {doc.title.split(" ").slice(0, -1).join(" ")} <AccentItalic>{doc.title.split(" ").slice(-1)[0]}</AccentItalic>
            </h1>

            <div className="flex flex-wrap items-center gap-4 mt-5">
              {doc.level && <LevelBadge level={({ foundational: 1, intermediate: 2, advanced: 3 })[doc.level] || 1} />}
              {doc.rating > 0 && <RatingStars value={doc.rating} count={doc.rating_count} />}
              {doc.duration_hours > 0 && <span className="text-[12.5px] text-text-dim font-mono uppercase tracking-[0.16em]">{doc.duration_hours} hours</span>}
              {doc.duration_days > 0 && <span className="text-[12.5px] text-text-dim font-mono uppercase tracking-[0.16em]">{doc.duration_days} days</span>}
              {doc.requires_initiation && <Badge variant="warning">Requires initiation</Badge>}
              {doc.consecrated && <Badge variant="primary">Consecrated</Badge>}
            </div>

            <p className="lede mt-6">{doc.summary || doc.abstract || doc.tagline}</p>

            {/* Premium image gallery (hero + thumbnails) */}
            {(activeImage || doc.hero_image_url) && (
              <button type="button" onClick={() => setLightbox(true)}
                      className="mt-8 block w-full rounded-2xl overflow-hidden border border-line-2 bg-surface cursor-zoom-in" data-testid="detail-hero-image">
                <img src={activeImage || doc.hero_image_url} alt={doc.title} className="w-full h-auto block max-h-[520px] object-cover" />
              </button>
            )}

            {doc.gallery_image_urls?.length > 0 && (
              <div className="mt-4 grid grid-cols-4 sm:grid-cols-5 gap-2.5" data-testid="detail-gallery">
                {[doc.hero_image_url, ...doc.gallery_image_urls].filter(Boolean).map((g, i) => {
                  const isActive = (activeImage || doc.hero_image_url) === g;
                  return (
                    <button key={i} type="button" onClick={() => setActiveImage(g)}
                      data-testid={`gallery-thumb-${i}`}
                      className={`block aspect-square rounded-lg overflow-hidden border transition-colors ${isActive ? "border-mauve ring-2 ring-mauve/50" : "border-line-2 hover:border-mauve"}`}>
                      <img src={g} alt={`${doc.title} – ${i + 1}`} className="w-full h-full object-cover" />
                    </button>
                  );
                })}
              </div>
            )}

            <Tabs defaultValue="overview" className="mt-10">
              <TabsList>
                <TabsTrigger value="overview">Overview</TabsTrigger>
                {doc.learning_outcomes?.length > 0 && <TabsTrigger value="outcomes">Learning outcomes</TabsTrigger>}
                {doc.curriculum?.length > 0 && <TabsTrigger value="curriculum">Curriculum</TabsTrigger>}
                {doc.requirements?.length > 0 && <TabsTrigger value="requirements">Requirements</TabsTrigger>}
                {doc.specifications?.length > 0 && <TabsTrigger value="specs">Specifications</TabsTrigger>}
                {doc.features?.length > 0 && <TabsTrigger value="features">Features</TabsTrigger>}
                {doc.whats_included?.length > 0 && <TabsTrigger value="included">What&apos;s included</TabsTrigger>}
                {doc.practice_outline?.length > 0 && <TabsTrigger value="practice">Practice outline</TabsTrigger>}
                {doc.faqs?.length > 0 && <TabsTrigger value="faq">FAQ</TabsTrigger>}
                {doc.authors_full?.length > 0 && <TabsTrigger value="authors">Authors</TabsTrigger>}
                {(doc.custom_sections || []).filter((s) => s && s.title && (s.content || "").trim()).map((s, i) => (
                  <TabsTrigger key={`cs-tab-${i}`} value={`cs-${i}`}>{s.title}</TabsTrigger>
                ))}
              </TabsList>
              <TabsContent value="overview">
                <div className="prose-nvl text-text-dim text-[15px] leading-[1.85]" dangerouslySetInnerHTML={{ __html: doc.description_html || `<p>${doc.summary || ""}</p>` }} />
              </TabsContent>
              {doc.learning_outcomes?.length > 0 && (
                <TabsContent value="outcomes">
                  <ul className="space-y-3">
                    {doc.learning_outcomes.map((o, i) => <li key={i} className="flex items-start gap-3 text-[14.5px]"><Check size={16} className="text-sage mt-1 flex-shrink-0" />{o}</li>)}
                  </ul>
                </TabsContent>
              )}
              {doc.specifications?.length > 0 && (
                <TabsContent value="specs">
                  <dl className="divide-y divide-line">
                    {doc.specifications.map((s, i) => (
                      <div key={i} className="grid grid-cols-[200px_1fr] py-3 gap-4">
                        <dt className="text-[12.5px] font-mono uppercase tracking-[0.16em] text-text-dim">{s.label}</dt>
                        <dd className="text-[14px]">{s.value}</dd>
                      </div>
                    ))}
                  </dl>
                </TabsContent>
              )}
              {doc.curriculum?.length > 0 && (
                <TabsContent value="curriculum">
                  <ol className="space-y-3 list-decimal pl-5 text-[14.5px] text-text-dim" data-testid="detail-curriculum">
                    {doc.curriculum.map((m, i) => <li key={i}>{m}</li>)}
                  </ol>
                </TabsContent>
              )}
              {doc.requirements?.length > 0 && (
                <TabsContent value="requirements">
                  <ul className="space-y-3" data-testid="detail-requirements">
                    {doc.requirements.map((r, i) => <li key={i} className="flex items-start gap-3 text-[14.5px]"><Check size={16} className="text-sage mt-1 flex-shrink-0" />{r}</li>)}
                  </ul>
                </TabsContent>
              )}
              {doc.features?.length > 0 && (
                <TabsContent value="features">
                  <ul className="space-y-3" data-testid="detail-features">
                    {doc.features.map((f, i) => <li key={i} className="flex items-start gap-3 text-[14.5px]"><Check size={16} className="text-sage mt-1 flex-shrink-0" />{f}</li>)}
                  </ul>
                </TabsContent>
              )}
              {doc.whats_included?.length > 0 && (
                <TabsContent value="included">
                  <ul className="space-y-3" data-testid="detail-whats-included">
                    {doc.whats_included.map((w, i) => <li key={i} className="flex items-start gap-3 text-[14.5px]"><Check size={16} className="text-sage mt-1 flex-shrink-0" />{w}</li>)}
                  </ul>
                </TabsContent>
              )}
              {doc.faqs?.length > 0 && (
                <TabsContent value="faq">
                  <div className="space-y-4" data-testid="detail-faq">
                    {doc.faqs.map((f, i) => (
                      <div key={i} className="rounded-xl border border-line-2 bg-surface p-4">
                        <div className="text-[14px] font-display mb-1.5">{f.label}</div>
                        <div className="text-[13.5px] text-text-dim leading-[1.7]">{f.value}</div>
                      </div>
                    ))}
                  </div>
                </TabsContent>
              )}
              {doc.practice_outline?.length > 0 && (
                <TabsContent value="practice">
                  <ol className="space-y-3 list-decimal pl-5 text-[14.5px] text-text-dim">
                    {doc.practice_outline.map((p, i) => <li key={i}>{p}</li>)}
                  </ol>
                </TabsContent>
              )}
              {doc.authors_full?.length > 0 && (
                <TabsContent value="authors">
                  <ul className="space-y-3">
                    {doc.authors_full.map((a) => (
                      <li key={a.id} className="flex items-center gap-3"><span className="w-9 h-9 rounded-full bg-mauve/30" />
                        <span><b className="text-[14px]">{a.name}</b><br /><span className="text-[12px] text-text-faint">{a.affiliation}</span></span>
                      </li>
                    ))}
                  </ul>
                </TabsContent>
              )}
              {(doc.custom_sections || []).filter((s) => s && s.title && (s.content || "").trim()).map((s, i) => (
                <TabsContent key={`cs-panel-${i}`} value={`cs-${i}`}>
                  <div className="text-[14.5px] leading-[1.75] text-text-dim whitespace-pre-line" data-testid={`custom-section-${i}`}
                       dangerouslySetInnerHTML={{ __html: s.content }} />
                </TabsContent>
              ))}
            </Tabs>
          </div>

          {/* Sidebar */}
          <aside className="lg:sticky lg:top-32 self-start">
            <Card variant="elevated">
              <div className="relative aspect-[16/10]">
                {(activeImage || doc.hero_image_url) ? (
                  <img src={activeImage || doc.hero_image_url} alt={doc.title} className="absolute inset-0 w-full h-full object-cover" data-testid="detail-sidebar-image" />
                ) : (
                  <ArtCard seed={(doc.title?.length || 0) % 5} motifSeed={2} />
                )}
              </div>
              <CardBody>
                {doc.is_free ? (
                  <Badge variant="success" size="lg">Free</Badge>
                ) : doc.price_inr > 0 ? (
                  <PriceBlock price={doc.price_inr} was={doc.compare_at_inr} size="lg" />
                ) : null}
                <div className="mt-5 flex flex-col gap-2.5">
                  {kind === "course" && (
                    <>
                      {doc.enrolled ? (
                        <Button asChild data-testid="continue-learning-btn"><Link to="/dashboard/courses"><BookOpen size={14}/> Continue learning</Link></Button>
                      ) : (
                        <Button onClick={onEnroll} data-testid="enroll-btn">{doc.is_free ? "Enroll free" : "Enroll now"} <ArrowRight size={14} /></Button>
                      )}
                      {!doc.is_free && !doc.enrolled && <Button variant="ghost" onClick={onAdd} data-testid="add-cart-btn"><ShoppingBag size={14} /> Add to cart</Button>}
                    </>
                  )}
                  {kind === "sadhana" && (
                    <Button onClick={onAdd} data-testid="add-cart-btn"><ShoppingBag size={14} /> Begin sadhana ({doc.duration_days} days)</Button>
                  )}
                  {kind === "product" && (
                    <Button onClick={onAdd} data-testid="add-cart-btn"><ShoppingBag size={14} /> {addLabel}</Button>
                  )}
                  {kind === "service" && (
                    <Button onClick={onBook} data-testid="book-now-btn"><Calendar size={14} /> Book this service</Button>
                  )}
                  {kind === "book" && (
                    <>
                      {doc.google_drive_url || doc.pdf_url ? (
                        <Button asChild data-testid="read-book-btn">
                          <a href={doc.google_drive_url || doc.pdf_url} target="_blank" rel="noopener noreferrer">
                            <BookOpen size={14}/> Read online
                          </a>
                        </Button>
                      ) : (
                        <Button asChild data-testid="read-book-btn">
                          <Link to={`/library/${doc.slug}/read`}><BookOpen size={14}/> Open reader</Link>
                        </Button>
                      )}
                      {(doc.pdf_url || doc.google_drive_url) && (
                        <Button asChild variant="ghost" data-testid="download-book-btn">
                          <a href={doc.pdf_url || doc.google_drive_url} download target="_blank" rel="noopener noreferrer">
                            <Download size={14}/> Download
                          </a>
                        </Button>
                      )}
                    </>
                  )}
                  {kind === "research" && (
                    <>
                      {doc.pdf_url ? (
                        <Button asChild data-testid="download-paper-btn">
                          <a href={doc.pdf_url} target="_blank" rel="noopener noreferrer" download>
                            <Download size={14}/> Download paper
                          </a>
                        </Button>
                      ) : (
                        <Button asChild data-testid="download-paper-btn">
                          <Link to={`/research/${doc.slug}/read`}><BookOpen size={14}/> Read in browser</Link>
                        </Button>
                      )}
                      {doc.citation && (
                        <Button variant="ghost" onClick={() => { navigator.clipboard?.writeText(doc.citation); toast({ title: "Citation copied" }); }} data-testid="copy-citation-btn">
                          Copy citation
                        </Button>
                      )}
                    </>
                  )}
                  <Button variant="ghost" onClick={onToggleWishlist} data-testid="wishlist-toggle-btn">
                    <Heart size={14} className={saved ? "fill-current" : ""} /> {saved ? "Saved to wishlist" : "Save to wishlist"}
                  </Button>
                </div>
              </CardBody>
            </Card>

            {doc.benefits?.length > 0 && (
              <div className="mt-5 p-5 rounded-2xl border border-line-2 bg-surface">
                <Eyebrow>What&apos;s included</Eyebrow>
                <ul className="mt-3 space-y-2 text-[13.5px]">
                  {doc.benefits.map((b, i) => <li key={i} className="flex items-start gap-2"><Check size={14} className="text-sage mt-1 flex-shrink-0" />{b}</li>)}
                </ul>
              </div>
            )}

            {kind === "course" && doc.instructor_name && (
              <div className="mt-5 p-5 rounded-2xl border border-line-2 bg-surface" data-testid="detail-instructor">
                <Eyebrow>Instructor</Eyebrow>
                <div className="mt-3 flex items-center gap-3">
                  <span className="w-11 h-11 rounded-full bg-mauve/30 flex items-center justify-center font-display text-[16px]">
                    {doc.instructor_name.charAt(0)}
                  </span>
                  <div>
                    <div className="text-[14px] font-display">{doc.instructor_name}</div>
                    {doc.language && <div className="text-[12px] text-text-faint mt-0.5">Language: {doc.language}</div>}
                  </div>
                </div>
              </div>
            )}
          </aside>
        </div>

        {/* Related */}
        {doc.related?.length > 0 && (
          <div className="mt-16">
            <Eyebrow>You may also like</Eyebrow>
            <h2 className="font-display text-[clamp(22px,2.6vw,30px)] mt-3 mb-6">Related</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {doc.related.map((r, i) => (
                <Link key={r.id} to={detailPath.replace(":slug", r.slug)} className="block">
                  <Card interactive className="h-full">
                    <div className="aspect-[16/10] relative overflow-hidden">
                      {r.hero_image_url ? (
                        <img src={r.hero_image_url} alt={r.title} className="absolute inset-0 w-full h-full object-cover" />
                      ) : (
                        <ArtCard seed={i % 5} motifSeed={(i + 2) % 4} />
                      )}
                    </div>
                    <CardBody>
                      <h3 className="font-display text-[18px] leading-tight">{r.title}</h3>
                      {r.price_inr > 0 && <div className="mt-3"><PriceBlock price={r.price_inr} size="sm" /></div>}
                    </CardBody>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Lightbox */}
        {lightbox && (activeImage || doc.hero_image_url) && (
          <div onClick={() => setLightbox(false)} role="dialog" aria-modal="true"
               className="fixed inset-0 z-[300] bg-black/90 backdrop-blur-sm flex items-center justify-center p-6 cursor-zoom-out"
               data-testid="detail-lightbox">
            <button onClick={(e) => { e.stopPropagation(); setLightbox(false); }}
                    className="absolute top-5 right-5 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white inline-flex items-center justify-center"
                    aria-label="Close lightbox" data-testid="lightbox-close">×</button>
            <img src={activeImage || doc.hero_image_url} alt={doc.title}
                 className="max-w-full max-h-full object-contain rounded-lg shadow-2xl" />
          </div>
        )}
      </Container>
    </Section>
  );
}
